import dayjs from "dayjs";

import type { MoodChatRunFilters, MoodChatRunSummary } from "./types";

export type ConversationGroup = {
  conversationId: string | null;
  runs: MoodChatRunSummary[];
};

/** Excel reads a CSV as the local ANSI codepage unless the file opens with a BOM. */
const UTF8_BOM = "﻿";

const CSV_COLUMNS = [
  "timestamp",
  "conversationId",
  "turn",
  "runId",
  "sessionId",
  "uid",
  "city",
  "country",
  "status",
  "userQuery",
  "reply",
  "toolCalls",
  "elapsedMs",
  "shownCount",
  "shownIds",
  "resultCount",
  "resultIds",
  "error",
] as const;

/**
 * Serialises runs to RFC 4180 CSV, one row per turn, grouped into conversations.
 * Replies contain commas, quotes and newlines as a matter of course, so every
 * field goes through {@link escapeCsvValue}.
 */
export function buildMoodChatLogsCsv(runs: MoodChatRunSummary[]): string {
  const rows = groupRunsIntoConversations(runs).flatMap((group) =>
    group.runs.map((run, index) => toCsvRow(run, group, index + 1)),
  );

  return UTF8_BOM + [CSV_COLUMNS.join(","), ...rows].join("\r\n");
}

/**
 * Formats the bounds in local time, so the name matches the days the user picked
 * in the date range rather than shifting a day in UTC.
 */
export function buildExportFilename(
  filters: Pick<MoodChatRunFilters, "from" | "to">,
  exportedAt: Date = new Date(),
): string {
  const from = toFilenameDate(filters.from, exportedAt);
  const to = toFilenameDate(filters.to, exportedAt);

  return `mood-chat-logs_${from}-${to}.csv`;
}

function toFilenameDate(value: string | undefined, fallback: Date): string {
  const date = value ? dayjs(value) : dayjs(fallback);
  return (date.isValid() ? date : dayjs(fallback)).format("YYYYMMDD");
}

function toCsvRow(
  run: MoodChatRunSummary,
  group: ConversationGroup,
  turn: number,
): string {
  const shownIds = run.referencedIds ?? [];
  const resultIds = getResultIds(run.toolTraces);

  return [
    toIsoUtc(run.timestamp),
    group.conversationId ?? "",
    turn,
    run.id,
    run.sessionId ?? "",
    run.uid ?? "",
    run.location?.city ?? "",
    run.location?.country ?? "",
    run.status,
    run.message ?? "",
    run.reply ?? "",
    run.toolCalls.join(" "),
    run.elapsedMs ?? "",
    shownIds.length,
    shownIds.join(" "),
    resultIds.length,
    resultIds.join(" "),
    toErrorText(run.error),
  ]
    .map(escapeCsvValue)
    .join(",");
}

/**
 * Every entity id the searches returned, deduped in first-seen order. Reads the
 * logged tool traces defensively — they are whatever Brain happened to log, and a
 * run that failed mid-tool can carry a trace with no output at all.
 */
function getResultIds(toolTraces: unknown): string[] {
  if (!Array.isArray(toolTraces)) return [];

  const ids = new Set<string>();

  toolTraces.forEach((trace) => {
    const items = asRecord(asRecord(trace)?.output)?.items;
    if (!Array.isArray(items)) return;

    items.forEach((item) => {
      const id = asRecord(item)?.id;
      if (typeof id === "string" && id) ids.add(id);
    });
  });

  return [...ids];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function escapeCsvValue(value: string | number): string {
  const text = String(value);

  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** ISO 8601 UTC — a data export has to sort lexicographically and be unambiguous. */
function toIsoUtc(value?: string | null): string {
  if (!value) return "";

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
}

/** Keeps whatever the error carried — a dropped error is a silent debugging dead end. */
function toErrorText(error: unknown): string {
  if (!error) return "";
  if (typeof error === "string") return error;

  if (typeof error === "object") {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") return message;
    return JSON.stringify(error);
  }

  return String(error);
}

/**
 * Groups runs into conversations for export: conversations ordered by their most
 * recent turn (newest first), turns ascending within each conversation so a
 * transcript reads top-to-bottom. Runs with no conversation id stay separate —
 * merging them would invent a conversation that never happened.
 */
export function groupRunsIntoConversations(
  runs: MoodChatRunSummary[],
): ConversationGroup[] {
  const groups = new Map<string, ConversationGroup>();

  runs.forEach((run, index) => {
    const conversationId = getConversationId(run);
    // Unprefixable key so a loose run can never collide with a real id.
    const key = conversationId ?? ` loose:${index}`;
    const group = groups.get(key);

    if (group) {
      group.runs.push(run);
      return;
    }

    groups.set(key, { conversationId, runs: [run] });
  });

  return [...groups.values()]
    .map((group) => ({
      ...group,
      runs: [...group.runs].sort(
        (a, b) => toSortableTime(a) - toSortableTime(b),
      ),
    }))
    .sort((a, b) => getLatestTime(b) - getLatestTime(a));
}

function getConversationId(run: MoodChatRunSummary): string | null {
  return run.conversationId || run.requestedConversationId || null;
}

/** Undated runs sort last within a conversation. */
function toSortableTime(run: MoodChatRunSummary): number {
  const time = run.timestamp ? Date.parse(run.timestamp) : Number.NaN;
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
}

/** Conversations with no usable timestamp sort last overall. */
function getLatestTime(group: ConversationGroup): number {
  return group.runs.reduce((latest, run) => {
    const time = run.timestamp ? Date.parse(run.timestamp) : Number.NaN;
    return Number.isNaN(time) ? latest : Math.max(latest, time);
  }, Number.NEGATIVE_INFINITY);
}
